import { useMedia } from '@dnb/eufemia/shared';
import { dereference } from '@portals/shared/common/open-api/resolver';
import type { OpenAPIV3 } from 'openapi-types';
import { useCallback, useEffect, useMemo, useState } from 'react';

import Search from '@/components/Guide/Search';
import { LinkType, methods, SearchItem, TagType } from '@/pages/api-documentation/constants/types';
import { ReferenceContentView } from '@/pages/api-documentation/sections/reference/components/ReferenceView/components/ReferenceContentView';

import Menu from './components/Menu';
import MobileMenu from './components/MobileMenu';

import { StyledApiDocumentationPage } from '@/pages/api-documentation/styles/Api.Reference.styles';

const ReferenceView = ({ reference, compact = false }: { reference: OpenAPIV3.Document; compact?: boolean }) => {
  const {
    info,
    paths,
    tags: refTags,
    components,
    servers,
    security,
  } = useMemo(() => (reference && dereference(reference)) || {}, [reference]);

  const [items, setItems] = useState<string[]>([]);
  const { isLarge } = useMedia();
  const [menuItems, setMenuItems] = useState<Record<string, LinkType[]>>();
  const [mobileMenu, setMobileMenu] = useState(false);
  const [tags, setTags] = useState<TagType[] | undefined>();
  const [search, setSearch] = useState<string[] | undefined>();
  const [searchItems, setSearchItems] = useState<SearchItem[] | undefined>();
  const [hasOperationsWithoutTag, setHasOperationsWithoutTag] = useState(false);

  // JUST REMOVE THE MOBILE MENU IF WE HIT LARGE
  useEffect(() => {
    isLarge && setMobileMenu(false);
  }, [isLarge]);

  const navigateTo = useCallback((hash = '', jump = false) => {
    // It is important that the url is updated without actual navigation, so that the page does not reload.
    if (globalThis.location.hash !== hash) {
      globalThis.window.history.replaceState(null, '', globalThis.location.pathname + hash);
    }
    if (jump) {
      document.getElementById(hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
    }
    setMobileMenu(false);
  }, []);

  useEffect(() => {
    if (items.length === 0) {
      const allItems: string[] = [];
      const allTags: TagType[] =
        (refTags &&
          refTags.map((tag) => {
            return {
              name: tag.name,
              href: tag.name.replaceAll(' ', '-'),
              description: tag?.description,
            };
          })) ||
        [];
      const allMenuItems: Record<string, LinkType[]> = {};
      const allSearchItems: SearchItem[] = [];

      // ADD ITEMS TO THE DISCOVERY LIST.
      allItems.push('introduction');
      if (components?.securitySchemes) {
        allItems.push('security-schemes');
        Object.keys(components?.securitySchemes).map((schema) => {
          allItems.push(`security-schemes/${schema}`);
        });
      }

      Object.entries(paths).forEach(([pathKey, pathItem]) => {
        if (!pathItem || typeof pathItem === 'string') return;

        methods.forEach((httpMethod) => {
          const operationObject = pathItem[httpMethod];
          if (!operationObject) return;

          const operationTag = operationObject?.tags?.[0];
          if (!operationTag) {
            setHasOperationsWithoutTag(true);
          }
          const operationTagHref = operationTag?.replaceAll(' ', '-');
          if (operationTag && operationTagHref && !allTags.some((tag) => tag.name === operationTag)) {
            allTags.push({
              name: operationTag,
              href: operationTagHref,
            });
          }
          const newMenuItemKey = operationTagHref ?? '';

          const keyPrefix = operationTagHref ? `tags/${newMenuItemKey}` : 'tags';
          const keySuffix = `${httpMethod}${pathKey}`.replaceAll('{', '').replaceAll('}', '');
          const key = `${keyPrefix}/operation/${keySuffix}`;

          allItems.push(keyPrefix, key);
          allSearchItems.push({
            name: keySuffix,
            description: operationObject.summary + ' ' + operationObject.description,
          });

          const newMenuItem = {
            path: keySuffix,
            operator: httpMethod,
            description: operationObject.description ?? '',
            name: operationObject.summary ?? '',
          };

          if (Object.keys(allMenuItems).includes(newMenuItemKey)) {
            allMenuItems[newMenuItemKey] = [...allMenuItems[newMenuItemKey]!, newMenuItem];
          } else {
            allMenuItems[newMenuItemKey] = [newMenuItem];
          }
        });
      });
      setTags(allTags);
      setSearchItems(allSearchItems);
      setMenuItems(allMenuItems);
      setItems(allItems);
    }
  }, [tags, components?.securitySchemes, items, paths, refTags]);

  useEffect(() => {
    if (reference) {
      setTimeout(() => {
        document.getElementById(globalThis.location.hash.replace('#', ''))?.scrollIntoView({ behavior: 'auto' });
      }, 500);
    }
  }, [reference]);

  return (
    <StyledApiDocumentationPage>
      <div className={`__left ${mobileMenu ? 'active' : ''}`}>
        <div className="sticker">
          <Search items={searchItems} searchResult={setSearch} />

          <div className="scroll-content">
            {tags && (
              <Menu
                compact={compact}
                components={components}
                items={items}
                menuItems={menuItems}
                mobile={!isLarge}
                navigateTo={navigateTo}
                paths={paths}
                search={search}
                tags={tags}
              />
            )}
          </div>
        </div>
      </div>
      <ReferenceContentView
        compact={compact}
        components={components}
        hasOperationsWithoutTag={hasOperationsWithoutTag}
        info={info}
        paths={paths}
        security={security}
        servers={servers}
        tags={tags}
      />
      <MobileMenu mobileMenu={mobileMenu} setMobileMenu={setMobileMenu} />
    </StyledApiDocumentationPage>
  );
};

export default ReferenceView;
